import React from 'react';
import { Heading, SimpleGrid, Box, Text, Button } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  CIL,
  SECTION_TITLE_CONTAINER_BG,
  SECTION_TITLE_TEXT_COLOR,
} from './constants';
import { calculateVariantAvailableSeats } from '../../utils';

const formatDateIt = (dateString) => {
  if (!dateString) return '–';
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/** Stessa logica dell'hero: verde ≥40%, arancione 0<x<40%, rosso sold out */
function getAvailabilityTextColor(available, capienza) {
  if (capienza == null || capienza === 0) return 'gray.600';
  if (available == null || available <= 0) return 'red.500';
  const pct = (available / capienza) * 100;
  if (pct >= 40) return 'green.600';
  return 'orange.600';
}

const WeeksSection = ({
  event,
  onSettimanaClick,
  onListaAttesaClick,
  isRegistrationOpen = true,
  userData,
  guestFlowActive = false,
}) => {
  const varianti = event?.Varianti?.length
    ? [...event.Varianti].sort((a, b) =>
        (a.Data_inizio || '').localeCompare(b.Data_inizio || '')
      )
    : [];

  const isLoggedIn = Boolean(userData);
  const canOpenDrawer = isLoggedIn || guestFlowActive;

  const weekCards = varianti.map((v) => {
    const available = calculateVariantAvailableSeats(v);
    const isSoldOut = available !== 999 && available <= 0;
    const dataFine = v.Data_fine || v.Data_Fine;
    const isBecomeSocioLink = !isSoldOut && !canOpenDrawer;

    return (
      <Box
        key={v.documentId}
        as={isSoldOut ? 'div' : isBecomeSocioLink ? RouterLink : 'button'}
        to={isBecomeSocioLink ? '/tesseramento' : undefined}
        type={isSoldOut || isBecomeSocioLink ? undefined : 'button'}
        onClick={
          isSoldOut || isBecomeSocioLink
            ? undefined
            : () => isRegistrationOpen && onSettimanaClick(v)
        }
        textAlign="left"
        display="block"
        w="100%"
        p={6}
        borderRadius="xl"
        bg={isSoldOut ? 'gray.50' : 'white'}
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.1), 0 8px 32px rgba(0, 0, 0, 0.06)"
        position="relative"
        role="group"
        _hover={
          !isSoldOut && (isBecomeSocioLink || isRegistrationOpen)
            ? {
                boxShadow:
                  '0 8px 28px rgba(0, 0, 0, 0.14), 0 12px 40px rgba(0, 0, 0, 0.08)',
                bg: 'gray.50',
                transform: 'scale(1.03)',
              }
            : {}
        }
        _disabled={{ cursor: 'not-allowed', opacity: 0.9 }}
        disabled={!isSoldOut && !isBecomeSocioLink && !isRegistrationOpen}
        transition="all 0.2s ease"
      >
        <Text fontWeight="bold" fontSize="md" color={CIL.plum}>
          {v.Nome || 'Variante'}
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          {dataFine
            ? `Da ${formatDateIt(v.Data_inizio)} a ${formatDateIt(dataFine)}`
            : `Da ${formatDateIt(v.Data_inizio)}`}
        </Text>
        {v.Capienza != null && (
          <Text
            fontSize="sm"
            fontWeight="bold"
            mt={2}
            color={getAvailabilityTextColor(available, v.Capienza)}
          >
            {isSoldOut ? 'Sold out' : `${available} posti disponibili`}
          </Text>
        )}
        {isSoldOut && onListaAttesaClick && (
          <Button
            size="sm"
            mt={3}
            colorScheme="blue"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onListaAttesaClick(v);
            }}
          >
            Lista d&apos;attesa
          </Button>
        )}
        {!isSoldOut && isRegistrationOpen && (
          <Box
            position="absolute"
            inset={0}
            borderRadius="xl"
            bg="rgba(255, 204, 0, 0.15)"
            opacity={0}
            _groupHover={{ opacity: 1 }}
            transition="opacity 0.2s ease"
            display="flex"
            alignItems="flex-end"
            justifyContent="flex-end"
            pointerEvents="none"
            p={3}
          >
            <Text fontWeight="bold" fontSize="sm" color={CIL.plum}>
              {isLoggedIn ? 'Prenota ora!' : 'Diventa socio per iscriverti'}
            </Text>
          </Box>
        )}
      </Box>
    );
  });

  return (
    <Box
      as="section"
      id="section-weeks"
      py={{ base: 10, md: 14 }}
      scrollMarginTop={{ base: '80px', md: '140px' }}
    >
      <Box w="100%" maxW="100%" px={{ base: 4, md: 6, lg: 8 }}>
        <Heading as="h2" size="lg" mb={8} textAlign="center">
          <Box
            w="100%"
            px={4}
            py={3}
            borderRadius="lg"
            sx={{ background: SECTION_TITLE_CONTAINER_BG }}
            boxShadow="sm"
          >
            <Text as="span" fontWeight="bold" color={SECTION_TITLE_TEXT_COLOR}>
              {varianti.length
                ? `Le ${varianti.length} settimane`
                : 'Le settimane'}
            </Text>
          </Box>
        </Heading>
        {varianti.length > 0 ? (
          <SimpleGrid
            columns={{ base: 1, md: 2, lg: 3 }}
            spacing={4}
            w="100%"
            maxW="100%"
          >
            {weekCards}
          </SimpleGrid>
        ) : (
          <Text textAlign="center" color="gray.500" py={8}>
            Nessuna settimana disponibile al momento.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default WeeksSection;
